import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { productService } from '../../services/productService';
import { Product, ProductFilters, PaginationInfo } from '../../types';
import { getProductImageUrl, hasProductImage } from '../../utils/imageUtils';
import ProductImage from '../../components/common/ProductImage';

const ProductManagement: React.FC = () => {
    const { user } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<PaginationInfo>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20
    });
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<Product>>({});

    useEffect(() => {
        loadProducts();
    }, [currentPage, searchTerm]);

    // Debug effect to monitor editFormData changes
    useEffect(() => {
        if (editingProduct) {
            console.log('editFormData changed:', editFormData);
            console.log('is_available value:', editFormData.is_available, 'Type:', typeof editFormData.is_available);
        }
    }, [editFormData, editingProduct]);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const filters: ProductFilters = {
                page: currentPage,
                limit: 20,
                search: searchTerm || undefined
            };

            // For admin, include unavailable products
            const response = await productService.getProducts(filters, true);
            setProducts(response.products);
            setPagination(response.pagination);
        } catch (error) {
            console.error('Erreur lors du chargement des produits:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        loadProducts();
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const handleEditProduct = (product: Product) => {
        console.log('Opening edit modal for product:', product.designation || product.name);
        console.log('Product is_available value:', product.is_available, 'Type:', typeof product.is_available);
        setEditingProduct(product);
        setEditFormData({
            designation: product.designation || product.name,
            description: product.description,
            unit_price: product.unit_price || product.price,
            weight: product.weight,
            cacherout: product.cacherout || product.kosher_certification,
            photo_url: product.photo_url || product.image_url,
            is_available: Boolean(product.is_available) // Ensure it's always a boolean
        });
    };

    const handleUpdateProduct = async () => {
        if (!editingProduct) return;

        console.log('Updating product with data:', editFormData);
        console.log('is_available value being sent:', editFormData.is_available);

        try {
            // Map frontend field names to backend expected names
            const backendData = {
                designation: editFormData.designation,
                description: editFormData.description,
                unitPrice: editFormData.unit_price, // Map unit_price to unitPrice
                weight: editFormData.weight,
                cacherout: editFormData.cacherout,
                photoUrl: editFormData.photo_url, // Map photo_url to photoUrl
                isAvailable: editFormData.is_available // Map is_available to isAvailable
            };

            console.log('Mapped backend data:', backendData);

            await productService.updateProduct(editingProduct.id, backendData);
            setEditingProduct(null);
            setEditFormData({});
            loadProducts(); // Reload the products list
            alert('Produit mis à jour avec succès !');
        } catch (error) {
            console.error('Erreur lors de la mise à jour du produit:', error);
            alert('Erreur lors de la mise à jour du produit');
        }
    };

    const handleDeleteProduct = async (product: Product) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le produit "${product.designation || product.name}" ?`)) {
            return;
        }

        try {
            await productService.deleteProduct(product.id);
            loadProducts(); // Reload the products list
            alert('Produit supprimé avec succès !');
        } catch (error) {
            console.error('Erreur lors de la suppression du produit:', error);
            alert('Erreur lors de la suppression du produit');
        }
    };

    const handleCancelEdit = () => {
        setEditingProduct(null);
        setEditFormData({});
    };

    const handleAvailabilityChange = (checked: boolean) => {
        console.log('handleAvailabilityChange called with:', checked);
        setEditFormData(prevData => {
            const newData = { ...prevData, is_available: checked };
            console.log('New form data will be:', newData);
            return newData;
        });
    };

    const handlePriceChange = (value: string) => {
        console.log('handlePriceChange called with:', value);
        setEditFormData(prevData => {
            // Handle empty string properly for better editing experience
            let numericValue: number;
            if (value === '' || value === null || value === undefined) {
                numericValue = 0;
            } else {
                const parsed = parseFloat(value);
                numericValue = isNaN(parsed) ? 0 : parsed;
            }
            const newData = { ...prevData, unit_price: numericValue };
            console.log('New price data will be:', newData);
            return newData;
        });
    };

    const handleFormFieldChange = (field: string, value: any) => {
        console.log(`handleFormFieldChange called for ${field} with:`, value);
        setEditFormData(prevData => {
            const newData = { ...prevData, [field]: value };
            console.log('New form data will be:', newData);
            return newData;
        });
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-eldai-gray-light">
                <div className="container mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center mb-4">
                            <Link
                                to="/admin"
                                className="text-eldai-blue hover:text-eldai-light-blue mr-4 flex items-center"
                            >
                                <span className="text-2xl mr-2">←</span> Retour au tableau de bord
                            </Link>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-eldai-blue to-eldai-yellow bg-opacity-10 rounded-lg p-6 shadow">
                            <div>
                                <h1 className="text-4xl font-extrabold text-eldai-blue mb-2 flex items-center">
                                    <span className="mr-3 text-eldai-yellow">🏷️</span> Gestion des produits
                                </h1>
                                <p className="text-eldai-gray text-lg">
                                    {pagination.totalItems} produits au total
                                </p>
                            </div>
                            <div className="mt-4 md:mt-0 flex items-center space-x-4">
                                <Link
                                    to="/admin/products/import"
                                    className="bg-eldai-yellow text-eldai-blue px-6 py-3 rounded-lg font-bold shadow hover:bg-eldai-blue hover:text-white transition-colors flex items-center"
                                >
                                    <span className="mr-2">📥</span> Importer CSV
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <form onSubmit={handleSearch} className="flex space-x-4">
                            <div className="flex-1">
                                <input
                                    type="text"
                                    placeholder="Rechercher un produit par nom, code ou certification..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                />
                            </div>
                            <button
                                type="submit"
                                className="bg-eldai-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                            >
                                🔍 Rechercher
                            </button>
                        </form>
                    </div>

                    {/* Products Table */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-eldai-gray-light">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-eldai-blue to-eldai-yellow bg-opacity-10">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Code</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Image</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Description</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Prix</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Disponibilité</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Catégorie</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Casher</th>
                                        <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-eldai-gray-light">
                                    {loading ? (
                                        [...Array(10)].map((_, i) => (
                                            <tr key={i} className="bg-eldai-gray-light">
                                                {[...Array(7)].map((_, j) => (
                                                    <td key={j} className="px-6 py-4">
                                                        <div className="h-4 bg-eldai-gray rounded animate-pulse"></div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : products.length > 0 ? (
                                        products.map((product, idx) => (
                                            <tr key={product.id} className={idx % 2 === 0 ? "bg-white hover:bg-eldai-gray-light" : "bg-eldai-gray-light hover:bg-eldai-yellow/10"}>
                                                <td className="px-6 py-4 text-sm font-mono text-eldai-blue">
                                                    {product.code}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center product-desc">
                                                        <ProductImage
                                                            product={product}
                                                            size="medium"
                                                            className="mr-3"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center product-desc">
                                                        <div className="text-container">
                                                            <p className="font-semibold text-eldai-blue">{product.designation || product.name}</p>
                                                            {product.weight && (
                                                                <p className="text-xs text-eldai-gray">{product.weight}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-base font-bold text-eldai-blue">
                                                    {formatPrice(product.unit_price || product.price || 0)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-sm font-bold shadow ${product.is_available
                                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                                        : 'bg-red-100 text-red-800 border border-red-200'
                                                        }`}>
                                                        {product.is_available ? 'Disponible' : 'Indisponible'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-eldai-gray">
                                                    {product.category_name || 'Non classé'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {product.cacherout || product.kosher_certification ? (
                                                        <span className="bg-eldai-yellow text-eldai-blue px-3 py-1 rounded-full text-xs font-bold shadow">
                                                            CASHER
                                                        </span>
                                                    ) : (
                                                        <span className="text-eldai-gray text-sm">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex space-x-2">
                                                        <Link
                                                            to={`/product/${product.code}`}
                                                            className="text-eldai-blue hover:text-eldai-light-blue text-xl"
                                                            title="Voir le produit"
                                                        >
                                                            👁️
                                                        </Link>
                                                        <button
                                                            onClick={() => handleEditProduct(product)}
                                                            className="text-yellow-600 hover:text-yellow-800 text-xl"
                                                            title="Modifier"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProduct(product)}
                                                            className="text-red-600 hover:text-red-800 text-xl"
                                                            title="Supprimer"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-16 text-center bg-eldai-gray-light rounded-b-xl">
                                                <div className="text-7xl mb-4">📦</div>
                                                <h3 className="text-2xl font-bold text-eldai-blue mb-2">
                                                    Aucun produit trouvé
                                                </h3>
                                                <p className="text-eldai-gray mb-4 text-lg">
                                                    {searchTerm
                                                        ? 'Aucun produit ne correspond à votre recherche.'
                                                        : 'Aucun produit dans le catalogue.'}
                                                </p>
                                                <Link
                                                    to="/admin/products/import"
                                                    className="bg-eldai-yellow text-eldai-blue px-8 py-3 rounded-lg font-bold hover:bg-eldai-blue hover:text-white transition-colors"
                                                >
                                                    📥 Importer des produits
                                                </Link>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-eldai-gray-light">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-eldai-gray">
                                        Page {pagination.currentPage} sur {pagination.totalPages}
                                        ({pagination.totalItems} produits au total)
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 border border-eldai-gray rounded text-sm hover:bg-eldai-gray-light disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Précédent
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                                            disabled={currentPage === pagination.totalPages}
                                            className="px-3 py-1 border border-eldai-gray rounded text-sm hover:bg-eldai-gray-light disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Edit Product Modal */}
                {editingProduct && createPortal(
                    <div
                        className="product-edit-modal"
                        onClick={(e) => {
                            // Close modal if clicking on backdrop
                            if (e.target === e.currentTarget) {
                                handleCancelEdit();
                            }
                        }}
                    >
                        <div className="product-edit-modal-content">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 border-b border-eldai-gray-light bg-eldai-gray-light">
                                <h2 className="text-xl font-bold text-eldai-blue">
                                    ✏️ Modifier le produit
                                </h2>
                                <button
                                    onClick={handleCancelEdit}
                                    className="text-eldai-gray hover:text-eldai-blue text-3xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-eldai-gray transition-colors"
                                    aria-label="Fermer"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body-scroll">
                                <form onSubmit={(e) => { e.preventDefault(); handleUpdateProduct(); }} className="space-y-6">
                                    {/* Product Info Grid */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    Nom du produit *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editFormData.designation || ''}
                                                    onChange={(e) => handleFormFieldChange('designation', e.target.value)}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    Prix (€) *
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={editFormData.unit_price || 0}
                                                    onChange={(e) => {
                                                        console.log('Price field changed, new value:', e.target.value);
                                                        console.log('Current editFormData before price change:', editFormData);
                                                        handlePriceChange(e.target.value);
                                                    }}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    Poids
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editFormData.weight || ''}
                                                    onChange={(e) => handleFormFieldChange('weight', e.target.value)}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    placeholder="ex: 500g, 1kg, 250ml"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    Certification casher
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editFormData.cacherout || ''}
                                                    onChange={(e) => handleFormFieldChange('cacherout', e.target.value)}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    placeholder="ex: Beth Din de Paris"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    URL de l'image
                                                </label>
                                                <input
                                                    type="url"
                                                    value={editFormData.photo_url || ''}
                                                    onChange={(e) => handleFormFieldChange('photo_url', e.target.value)}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    placeholder="https://exemple.com/image.jpg"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-eldai-blue mb-2">
                                                    Description
                                                </label>
                                                <textarea
                                                    value={editFormData.description || ''}
                                                    onChange={(e) => handleFormFieldChange('description', e.target.value)}
                                                    className="w-full px-3 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-yellow focus:border-eldai-yellow"
                                                    rows={4}
                                                    placeholder="Description détaillée du produit..."
                                                />
                                            </div>

                                            <div className="flex items-center space-x-3 p-3 bg-eldai-gray-light rounded-lg">
                                                <input
                                                    type="checkbox"
                                                    id="is_available_checkbox"
                                                    checked={Boolean(editFormData.is_available)}
                                                    onChange={(e) => {
                                                        console.log('Checkbox clicked, new value:', e.target.checked);
                                                        console.log('Current editFormData before change:', editFormData);
                                                        console.log('Current is_available value:', editFormData.is_available);
                                                        handleAvailabilityChange(e.target.checked);
                                                        // Force a re-render check
                                                        setTimeout(() => {
                                                            console.log('editFormData after state update:', editFormData);
                                                        }, 0);
                                                    }}
                                                    className="w-4 h-4 text-eldai-yellow border-eldai-gray rounded focus:ring-eldai-yellow"
                                                />
                                                <label htmlFor="is_available_checkbox" className="text-sm font-medium text-eldai-blue">
                                                    Produit disponible à la vente (Actuellement: {editFormData.is_available ? 'Oui' : 'Non'})
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Modal Footer - Buttons */}
                                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-eldai-gray-light">
                                        <button
                                            type="button"
                                            onClick={handleCancelEdit}
                                            className="w-full sm:w-auto px-6 py-2 border border-eldai-gray text-eldai-gray rounded-lg hover:bg-eldai-gray-light transition-colors"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            className="w-full sm:w-auto px-6 py-2 bg-eldai-yellow text-eldai-blue rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                                        >
                                            💾 Sauvegarder les modifications
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </>
    );
};

export default ProductManagement;
